/**
 * This event is the client side equivalent of the DOMContentLoaded event.
 * Previously, when custom code was rendered server side, it could listen to
 * DOMContentLoaded and assume that all the block roots are present and all scripts
 * have been executed. Now we need to wait for page renderer before notifying
 * the custom code that the DOM is ready.
 * This is the name of the custom event that we use for that.
 */
export const SOFTR_PAGE_RENDERER_DOM_CONTENT_LOADED = "@softr-page-renderer/DOMContentLoaded";
export const SOFTR_PAGE_RENDERER_LOAD = "@softr-page-renderer/load";
export const SOFTR_PAGE_RENDERER_BLOCK_UNMOUNT = "@softr-page-renderer/block-unmount";

export function dispatchReactBlockUnmountEvent(detail: ReactBlockUnmountEvent["detail"]) {
    window.dispatchEvent(new CustomEvent(SOFTR_PAGE_RENDERER_BLOCK_UNMOUNT, { detail }));
}

/**
 * This custom event is fired by the React blocks when they have fetched the
 * component script, and rendered the block using the fetched component.
 * This can be used to wait for the DOM elements rendered by the fetched
 * components (as opposed to empty block roots.)
 */
const REACT_COMPONENT_READY_EVENT = "@softr-page-renderer/ReactComponentReady";
export function dispatchReactBlockReadyEvent(detail: ReactBlockReadyEvent["detail"]) {
    window.dispatchEvent(new CustomEvent(REACT_COMPONENT_READY_EVENT, { detail }));
}
export function addReactBlockReadyEventListener(callback: (event: ReactBlockReadyEvent) => void): {
    dispose: () => void;
} {
    const listener = (event: ReactBlockReadyEvent) => {
        callback(event);
    };
    // @ts-expect-error: Suppressed automatically in TS 5.8 to 6 migration
    window.addEventListener(REACT_COMPONENT_READY_EVENT, listener);
    return {
        dispose() {
            // @ts-expect-error: Suppressed automatically in TS 5.8 to 6 migration
            window.removeEventListener(REACT_COMPONENT_READY_EVENT, listener);
        },
    };
}

export function addReactBlockUnmountEventListener(callback: (event: ReactBlockUnmountEvent) => void): {
    dispose: () => void;
} {
    const listener = (event: ReactBlockUnmountEvent) => {
        callback(event);
    };
    // @ts-expect-error: Suppressed automatically in TS 5.8 to 6 migration
    window.addEventListener(SOFTR_PAGE_RENDERER_BLOCK_UNMOUNT, listener);
    return {
        dispose() {
            // @ts-expect-error: Suppressed automatically in TS 5.8 to 6 migration
            window.removeEventListener(SOFTR_PAGE_RENDERER_BLOCK_UNMOUNT, listener);
        },
    };
}

export type ReactBlockReadyEvent = CustomEvent<{ blockId: string }>;
export type ReactBlockUnmountEvent = CustomEvent<{ blockId: string }>;
